import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env, FIRST_PAGE } from '@noor/config';
import {
  multiorderParamsQueries,
  searchSettingsSearchSchema,
  type SearchSettingsSearch,
} from '@/entities/search-settings';
import { MultiorderParamsPage } from '@/pages/settings';

/**
 * Multiorder parameters — history paging lives in the URL. Prefetch is best-effort and
 * non-blocking: the page renders its shell at once and each section shows its
 * own loading/error state.
 */
export const Route = createFileRoute('/settings/multiorder')({
  staticData: { permission: 'multiorder_params.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: searchSettingsSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    // The form is always mounted, so the active version is always needed; the
    // history only when its tab is the landing one.
    void context.queryClient
      .prefetchQuery(multiorderParamsQueries.active())
      .catch(() => undefined);
    if (deps.tab === 'history') {
      void context.queryClient
        .prefetchQuery(multiorderParamsQueries.history(deps.page, deps.pageSize))
        .catch(() => undefined);
    }
  },
  component: RouteMultiorderParamsPage,
});

function RouteMultiorderParamsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onParamsChange = (next: Partial<SearchSettingsSearch>) => {
    const resetPage = !('page' in next) && search.page !== FIRST_PAGE;
    void navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        ...(resetPage ? { page: FIRST_PAGE } : {}),
      }),
    });
  };

  return <MultiorderParamsPage params={search} onParamsChange={onParamsChange} />;
}
